import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthUserContext } from "../../../context/AuthUserContext";
import { useNavigation } from "../../../utils/navigation";
import Spinner from "../../../components/animations/Spinner";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getProductTitle } from "../../../utils/productDisplayName";

const STATUS_COLORS = {
  pending: { bg: "#fff3cd", text: "#856404" },
  paid: { bg: "var(--primary-300)", text: "var(--primary-100)" },
  shipped: { bg: "#d6ecff", text: "#1868a8" },
  delivered: { bg: "#dcf5e6", text: "#1a7f4b" },
  canceled: { bg: "#fbdcdc", text: "var(--error-color)" },
};
const getStatusColors = (status) =>
  STATUS_COLORS[(status || "").toLowerCase()] || {
    bg: "var(--bg-300)",
    text: "var(--text-100)",
  };

const Title = styled.h2`
  color: var(--text-100);
  padding: var(--spacing-md) 0;
`;
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: var(--spacing-xl) 0;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xxl) var(--spacing-md);
  text-align: center;
  background-color: var(--bg-200);
  border-radius: var(--border-radius-base);
`;
const EmptyTitle = styled.h4`
  margin: 0;
  color: var(--text-100);
`;
const EmptySubtitle = styled.p`
  margin: 0;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;
const EmptyButton = styled.button`
  margin-top: var(--spacing-xs);
  background-color: var(--primary-100);
  color: var(--bg-100);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-small);
  text-transform: uppercase;
  font-size: var(--font-size-small);
`;

const OrderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  width: 100%;
`;
const OrderContainer = styled.figure`
  background-color: var(--bg-100);
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-base);
  transition: box-shadow 0.15s ease-in-out;
  &:hover {
    box-shadow: var(--shadow-medium);
  }
`;
const OrderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  cursor: pointer;
  flex-wrap: wrap;
`;
const OrderHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const OrderId = styled.span`
  font-weight: 700;
  color: var(--text-100);
`;
const OrderDate = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-200);
`;
const StatusBadge = styled.span`
  font-size: var(--font-size-small);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 5px 12px;
  border-radius: var(--border-radius-large);
  background-color: ${(props) => props.$bg};
  color: ${(props) => props.$text};
`;
const OrderTotal = styled.span`
  font-weight: 800;
  font-size: var(--font-size-large);
  color: var(--text-100);
`;
const Arrow = styled.svg`
  cursor: pointer;
  transform: ${(props) => (props.$open ? "rotate(90deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease-in-out;
  flex-shrink: 0;
`;
const OrderBody = styled.div`
  border-top: 1px solid var(--bg-300);
  background-color: var(--bg-200);
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;
const DetailsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
`;
const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 180px;
`;
const DetailTitle = styled.span`
  font-size: var(--font-size-small);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-200);
  margin-bottom: 4px;
`;
const DetailText = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-100);
`;
const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  border-top: 1px solid var(--bg-300);
  padding-top: var(--spacing-sm);
`;
const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background-color: var(--bg-100);
  border-radius: var(--border-radius-small);
  padding: var(--spacing-xs);
`;
const ItemImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  background-color: var(--bg-200);
  border-radius: var(--border-radius-small);
  flex-shrink: 0;
`;
const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
const ItemName = styled.span`
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--text-100);
`;
const ItemMeta = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-200);
`;
const ItemPrice = styled.span`
  font-size: var(--font-size-small);
  font-weight: 700;
  color: var(--text-100);
  white-space: nowrap;
`;

const OrderHistory = () => {
  const { t } = useTranslation();
  const { userProfile } = useContext(AuthUserContext);
  const [expandedOrders, setExpandedOrders] = useState({});
  const navigate = useNavigate();
  const { goToShop } = useNavigation();

  const toggleOrder = (orderId) => {
    setExpandedOrders((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${day.toString().padStart(2, "0")}.${month
      .toString()
      .padStart(2, "0")}.${year}`;
  };

  if (!userProfile) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  const orders = userProfile.order_history || [];

  return (
    <div>
      <Title>{t("ORDER_HISTORY.TITLE")}</Title>
      {orders.length === 0 ? (
        <EmptyContainer>
          <EmptyTitle>{t("ORDER_HISTORY.EMPTY_TITLE")}</EmptyTitle>
          <EmptySubtitle>{t("ORDER_HISTORY.NO_ORDERS")}</EmptySubtitle>
          <EmptyButton onClick={() => navigate(goToShop())}>
            {t("BUTTONS.SHOP_NOW")}
          </EmptyButton>
        </EmptyContainer>
      ) : (
        <OrderList>
          {orders.map((order) => {
            const isOpen = !!expandedOrders[order.id];
            const statusColors = getStatusColors(order.order_status);
            return (
              <OrderContainer key={order.id}>
                <OrderHeader onClick={() => toggleOrder(order.id)}>
                  <OrderHeaderInfo>
                    <OrderId>
                      {t("ORDER_HISTORY.ORDER_ID")} #{order.id}
                    </OrderId>
                    <OrderDate>{formatDate(order.created_at)}</OrderDate>
                  </OrderHeaderInfo>
                  <StatusBadge $bg={statusColors.bg} $text={statusColors.text}>
                    {order.order_status}
                  </StatusBadge>
                  <OrderTotal>
                    {order.currency_symbol}
                    {order.total_price}
                  </OrderTotal>
                  <Arrow
                    $open={isOpen}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 6L15 12L9 18"
                      stroke="var(--text-100)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Arrow>
                </OrderHeader>
                {isOpen && (
                  <OrderBody>
                    <DetailsGrid>
                      {order.address_details && (
                        <DetailsColumn>
                          <DetailTitle>
                            {t("ORDER_HISTORY.SHIPPING_ADDRESS")}
                          </DetailTitle>
                          <DetailText>
                            {order.address_details.street}{" "}
                            {order.address_details.building_number}
                          </DetailText>
                          <DetailText>
                            {order.address_details.postal_code}{" "}
                            {order.address_details.city}
                          </DetailText>
                          <DetailText>
                            {order.address_details.country}
                          </DetailText>
                        </DetailsColumn>
                      )}
                      <DetailsColumn>
                        <DetailTitle>
                          {t("ORDER_HISTORY.PAYMENT_METHOD")}
                        </DetailTitle>
                        <DetailText>{order.payment_method}</DetailText>
                        <DetailTitle>
                          {t("ORDER_HISTORY.SHIPPING_COST")}
                        </DetailTitle>
                        <DetailText>
                          {order.currency_symbol}
                          {order.shipping_cost}
                        </DetailText>
                      </DetailsColumn>
                    </DetailsGrid>
                    <ItemsList>
                      {order.order_items.map((item, index) => {
                        const product = item.product_details;
                        const images = product?.images || [];
                        const primaryImage =
                          images.find((img) => img.is_primary) || images[0];
                        return (
                          <ItemRow key={index}>
                            {primaryImage && (
                              <ItemImage
                                src={primaryImage.thumbnail}
                                alt={product?.name || ""}
                              />
                            )}
                            <ItemInfo>
                              <ItemName>
                                {getProductTitle(product?.category_name, product?.name)}
                              </ItemName>
                              <ItemMeta>
                                {t("ORDER_HISTORY.QUANTITY")}: {item.quantity}
                              </ItemMeta>
                            </ItemInfo>
                            <ItemPrice>
                              {order.currency_symbol}
                              {item.price}
                            </ItemPrice>
                          </ItemRow>
                        );
                      })}
                    </ItemsList>
                  </OrderBody>
                )}
              </OrderContainer>
            );
          })}
        </OrderList>
      )}
    </div>
  );
};

export default OrderHistory;
